import { formatCurrency } from '@applyboard/ui-utils';
import { ApplyShopStoreProductWithVariants } from '../data/applyshopStore/v3/types';
import { Country } from '../data/countries/useCountries';
import { convertIsoToCountryName } from './convertIsoToCountryName';

/**
 * Currency mapping for formatCurrency function
 * Based on existing patterns in the codebase
 */
const CURRENCY_MAP: Record<string, { code: string; sign: string; prefix: boolean }> = {
  USD: { code: 'USD', sign: '$', prefix: true },
  INR: { code: 'INR', sign: '₹', prefix: true },
  CAD: { code: 'CAD', sign: '$', prefix: true },
  EUR: { code: 'EUR', sign: '€', prefix: true },
  GBP: { code: 'GBP', sign: '£', prefix: true },
};

/**
 * Helper function to format a single price amount
 * @param amount - Price amount as string (e.g., "199.00")
 * @param currencyCode - Currency code (e.g., "USD", "INR")
 * @returns Formatted price string (e.g., "$199.00 USD") or null if invalid input
 */
const formatSinglePrice = (
  amount: string | undefined,
  currencyCode: string | undefined,
): string | null => {
  if (!amount || !currencyCode) {
    return null;
  }

  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) {
    return null;
  }

  const currency = CURRENCY_MAP[currencyCode];
  if (!currency) {
    // Fallback for unsupported currencies
    return `${numericAmount.toFixed(2)} ${currencyCode}`;
  }

  // Convert to cents for formatCurrency function (expects amount in cents)
  const amountInCents = Math.round(numericAmount * 100);

  return formatCurrency(amountInCents, currency, true, true, true);
};

/**
 * Gets pricing information for test card display with both full and discounted prices
 * @param discountedAmount - Discounted price amount as string (e.g., "199.00")
 * @param fullAmount - Full price amount as string (e.g., "249.00")
 * @param currencyCode - Currency code (e.g., "USD", "INR")
 * @returns Object with formatted prices for display or null if invalid input
 */
export const getTestCardPricing = (
  discountedAmount: string | undefined,
  fullAmount: string | undefined,
  currencyCode: string | undefined,
): { fullPrice: string; discountedPrice: string; hasDiscount: boolean } | null => {
  const discountedPrice = formatSinglePrice(discountedAmount, currencyCode);
  if (!discountedPrice) {
    return null;
  }

  // If no full price or same as discounted, just return discounted price
  if (!fullAmount || fullAmount === discountedAmount) {
    return {
      fullPrice: discountedPrice,
      discountedPrice,
      hasDiscount: false,
    };
  }

  const fullPrice = formatSinglePrice(fullAmount, currencyCode);
  if (!fullPrice) {
    return {
      fullPrice: discountedPrice,
      discountedPrice,
      hasDiscount: false,
    };
  }

  return {
    fullPrice,
    discountedPrice,
    hasDiscount: true,
  };
};

/**
 * Gets the best available price with both full and discounted prices from a product's variants
 * @param product - Shopify product with variants
 * @param preferredIsoCodes - Array of preferred ISO country codes (e.g., ["NP", "IN"])
 * @param countries - Optional countries array from the API for ISO to name conversion
 * @returns Price object with full and discounted amounts and currency, or null if no price available
 */
export const getBestAvailablePrices = (
  product: ApplyShopStoreProductWithVariants | undefined,
  preferredIsoCodes: string[] = [],
  countries?: Country[],
): { fullAmount: string; discountedAmount: string; currencyCode: string } | null => {
  if (!product || !product.variants || product.variants.length === 0) {
    return null;
  }

  // Convert ISO codes to country names for matching against Shopify variants
  const preferredCountryNames = preferredIsoCodes.map(isoCode =>
    convertIsoToCountryName(isoCode, countries),
  );
  const normalizedPrefs = new Set(preferredCountryNames.map(c => c.toLowerCase()));
  const variants = product.variants;

  const pickPrices = (variant?: (typeof variants)[number]) => {
    if (!variant?.discountedPrice) return null;

    return {
      fullAmount: variant.fullPrice?.amount || variant.discountedPrice.amount,
      discountedAmount: variant.discountedPrice.amount,
      currencyCode: variant.discountedPrice.currencyCode,
    };
  };

  // Special handling for India: use first variant directly since they use "Default Title"
  // Need to add Nepal here once vas_nepal_shoppify_store is enabled
  if (normalizedPrefs.has('india')) {
    const firstVariant = variants[0];
    const firstVariantPrices = pickPrices(firstVariant);
    if (firstVariantPrices) return firstVariantPrices;
  }

  // 1) Prefer available variant that matches preferred nationality
  const preferredAvailable =
    variants.find(
      v =>
        normalizedPrefs.size > 0 && normalizedPrefs.has(v.country.toLowerCase()) && v.isAvailable,
    ) || null;
  const preferredAvailablePrices = pickPrices(preferredAvailable || undefined);
  if (preferredAvailablePrices) return preferredAvailablePrices;

  // 2) Else any variant that matches preferred nationality
  const preferredAny =
    variants.find(v => normalizedPrefs.size > 0 && normalizedPrefs.has(v.country.toLowerCase())) ||
    null;
  const preferredAnyPrices = pickPrices(preferredAny || undefined);
  if (preferredAnyPrices) return preferredAnyPrices;

  // 3) No fallback - return null if no preferred variants
  return null;
};

/**
 * Checks if a product is sold out by examining both product-level and variant-level availability
 * @param product - Shopify product with variants
 * @param preferredIsoCodes - Array of preferred ISO country codes (e.g., ["NP", "IN"])
 * @param countries - Optional countries array from the API for ISO to name conversion
 * @returns true if product is sold out, false if available or not applicable
 */
export const isProductSoldOut = (
  product: ApplyShopStoreProductWithVariants | undefined,
  preferredIsoCodes: string[] = [],
  countries?: Country[],
): boolean => {
  if (!product) {
    return false;
  }

  if (!product.isAvailable) {
    return true;
  }

  if (!product.variants || product.variants.length === 0) {
    return true;
  }

  // Convert ISO codes to country names for matching against Shopify variants
  const preferredCountryNames = preferredIsoCodes.map(isoCode =>
    convertIsoToCountryName(isoCode, countries),
  );
  const normalizedPrefs = new Set(preferredCountryNames.map(c => c.toLowerCase()));
  const variants = product.variants;

  // Special handling for India: use first variant directly since they use "Default Title"
  // Need to add Nepal here once vas_nepal_shoppify_store is enabled
  if (normalizedPrefs.has('india')) {
    const firstVariant = variants[0];
    return !firstVariant?.isAvailable;
  }

  // For non-India countries: check availability based on preferred nationality
  if (normalizedPrefs.size > 0) {
    // 1) Check if there's any available variant that matches preferred nationality
    const hasPreferredAvailable = variants.some(
      v => normalizedPrefs.has(v.country.toLowerCase()) && v.isAvailable,
    );
    if (hasPreferredAvailable) {
      return false;
    }

    // 2) No preferred variants found - consider sold out for this nationality
    return true;
  }

  // Fallback: no preferred countries specified, just check for any available variant
  // This is to just show the button on the card since we're directing to product page
  return !variants.some(variant => variant.isAvailable);
};
